import { WorkoutExerciseMetric } from '@strength/domain-workout';
import { lcmOfArray } from '../../components/sheet/utils';
import { exercisesForSection } from './utils';
import { exerciseColumnCount } from './slots/exercises';
import { isLastExerciseInGroup } from './utils';
import { BlockStatus } from '@strength/domain';
import { checkIfReadOnly } from './slots/workout';
import { CSSProperties } from 'react';
import { isExerciseCollapsed, isExerciseHidden } from './slots/common';
import { getKeyForSectionCollapse } from './slots/common';
import { SortedWorkout, WorkoutWithCycleAndSession } from './types';

export type ExerciseHeight = {
  height: number;
  isReadOnly?: boolean;
};

// Calculates grid dimensions by finding the maximum sets columns across all workouts
// and computing the LCM of all unique non-sets metric counts to ensure
// consistent column sizing and alignment across the entire sheet
export function calculateSheetDimensions(
  workouts: WorkoutWithCycleAndSession[],
  profileId: string | null,
  blockStatus: BlockStatus = 'planned',
  collapsedComponents: {
    collapsedSections: Set<string>;
    collapsedExercises: Set<string>;
  } = { collapsedSections: new Set(), collapsedExercises: new Set() },
  showActuals = false,
): {
  maxSetsColumns: number;
  maxNonSetsColumns: number;
  greatestExerciseHeightsInSections: Array<Array<ExerciseHeight>>;
  maxMetricsPerExercise: number;
} {
  let maxSetsColumns = 0;
  const uniqueNonSetsMetricCounts = new Set<number>();

  const { collapsedSections, collapsedExercises } = collapsedComponents;

  // Calculate greatest exercise heights in sections
  const greatestExerciseHeightsInSections: Array<Array<ExerciseHeight>> = [];

  for (const workout of workouts) {
    // Force read-only when showActuals is enabled
    const isReadOnly =
      showActuals || checkIfReadOnly(workout, blockStatus);

    for (
      let sectionIndex = 0;
      sectionIndex < workout.sections.length;
      sectionIndex++
    ) {
      const section = workout.sections[sectionIndex];
      if (!section) continue;

      // Skip exercises in collapsed sections
      const isSectionCollapsed = collapsedSections.has(
        getKeyForSectionCollapse(section, profileId),
      );

      let exerciseHeightsByIndex =
        greatestExerciseHeightsInSections[sectionIndex];
      if (!exerciseHeightsByIndex) {
        exerciseHeightsByIndex = [];
        greatestExerciseHeightsInSections[sectionIndex] =
          exerciseHeightsByIndex;
      }

      // Skip exercises if section is collapsed
      if (!isSectionCollapsed) {
        const exercises = exercisesForSection(section);
        const filteredExercises = exercises.filter(
          (e) =>
            !isExerciseHidden(
              isSectionCollapsed,
              e.exercise,
              e.alternateGroup,
              profileId,
              collapsedExercises,
            ),
        );

        for (
          let exerciseIndex = 0;
          exerciseIndex < filteredExercises.length;
          exerciseIndex++
        ) {
          const exerciseData = filteredExercises[exerciseIndex];
          if (!exerciseData) continue;

          const { exercise, alternateGroup } = exerciseData;
          const isCollapsedExercise = isExerciseCollapsed(
            exercise,
            profileId,
            collapsedExercises,
          );

          if (exercise.metrics.length && !isCollapsedExercise) {
            const exerciseHasSets = exercise.metrics.some(
              (m: WorkoutExerciseMetric) => m.metricType === 'sets',
            );
            const exerciseNonSetsCount = exercise.metrics.filter(
              (m: WorkoutExerciseMetric) => m.metricType !== 'sets',
            ).length;

            if (exerciseHasSets) {
              maxSetsColumns = Math.max(maxSetsColumns, 1);
            }

            if (exerciseNonSetsCount > 0) {
              uniqueNonSetsMetricCounts.add(exerciseNonSetsCount);
            }
          }

          const currentHeight = exerciseHeightsByIndex[exerciseIndex] ?? {
            height: 0,
          };

          // if exercise is collapsed, set height to 1 (exercise label)
          const newHeight = isCollapsedExercise
            ? 1
            : exerciseColumnCount(
                exercise,
                isReadOnly,
                isLastExerciseInGroup(
                  alternateGroup,
                  exercise.workoutExerciseId,
                ),
              );
          if (
            newHeight > currentHeight.height ||
            (newHeight === currentHeight.height && !isReadOnly)
          ) {
            // If the new height is greater, or the same height but the new workout is not read only
            // then we want to update the max height. In the case of not being readonly this means that
            // we have to account for the additional buttons for adding a set/exercise/section
            exerciseHeightsByIndex[exerciseIndex] = {
              height: newHeight,
              isReadOnly: isReadOnly,
            };
          }
        }
      }
    }

    if (workout.sections.length === 0) {
      maxSetsColumns = Math.max(maxSetsColumns, 1);
      uniqueNonSetsMetricCounts.add(3);
    }
  }

  const allNonSetsArray = Array.from(uniqueNonSetsMetricCounts);

  const DEFAULT_NON_SETS = 3;
  const maxNonSets =
    allNonSetsArray.length > 0
      ? Math.max(...allNonSetsArray)
      : DEFAULT_NON_SETS;
  return {
    maxSetsColumns,
    maxNonSetsColumns:
      allNonSetsArray.length > 0
        ? lcmOfArray(allNonSetsArray)
        : DEFAULT_NON_SETS,
    greatestExerciseHeightsInSections,
    maxMetricsPerExercise: maxNonSets + maxSetsColumns,
  };
}

export function getGridTemplateColumns(
  workouts: WorkoutWithCycleAndSession[],
  maxNonSetsColumns: number,
  maxMetricsPerExercise: number,
  blockStatus: BlockStatus = 'planned',
  showActuals = false,
) {
  // 75px is what the sheet uses as the base width for a column
  // repeat(${columnCount * gridSubRows}, minmax(${75 / gridSubRows}px, 1fr))
  const maxWidthPerMetric = maxMetricsPerExercise * 75;
  const minWidth = Math.max(
    75 / maxNonSetsColumns,
    maxWidthPerMetric / maxNonSetsColumns,
  );

  let gridTemplateColumns = '';
  for (const workout of workouts) {
    // our logic to show sets follows the same logic as a Read Only workout. We show sets if a workout is not read only
    // Also hide sets when showing actuals
    const shouldShowSets =
      !checkIfReadOnly(workout, blockStatus) && !showActuals;
    const setsColumn = shouldShowSets ? '40px ' : '';
    const nonSetsColumns =
      maxNonSetsColumns > 0
        ? `repeat(${maxNonSetsColumns}, minmax(${minWidth}px, 1fr))`
        : '';
    const workoutTemplate = setsColumn + nonSetsColumns;
    gridTemplateColumns += workoutTemplate + ' ';
  }

  return gridTemplateColumns.trim();
}

/**
 * Calculates Y coordinates and heights for workout sections and exercises in a grid layout.
 *
 * This function determines the vertical positioning of each section and exercise row,
 * accounting for section headers, exercise heights, and "add" cells for new items.
 * It's used to create a CSS grid template that properly aligns workout content on the session view.
 */
export function getYs(
  greatestExerciseHeightsInSections: Array<Array<ExerciseHeight>>,
  workouts: SortedWorkout[] = [],
  collapsedSections: Set<string> = new Set(),
  profileId: string | null,
) {
  const sectionAndExerciseYs: Array<{
    sectionData: {
      y: number;
      height: number;
    };
    exercises: {
      y: number;
      height: number;
    }[];
  }> = [];

  const maxY = 0;

  let curY = 3; // Start at 3 because of workout label and assigned date and custom workout name
  // Reserve the add-exercise / add-section rows whenever any workout is still
  // editable — those cells render for editable workouts regardless of which
  // workout happens to be the tallest. (Previously this keyed off the tallest
  // last exercise's read-only flag, which cut the rows off once an expanded
  // read-only workout — canceled or completed — became the tallest in the grid.)
  const allWorkoutsReadOnly = workouts.every((w) => w.isReadOnly ?? false);
  for (
    let sectionIndex = 0;
    sectionIndex < greatestExerciseHeightsInSections.length;
    sectionIndex++
  ) {
    const exerciseHeightsInSection =
      greatestExerciseHeightsInSections[sectionIndex];
    if (!exerciseHeightsInSection) {
      continue;
    }
    const initY = curY;
    const newSectionYs: (typeof sectionAndExerciseYs)[0] = {
      sectionData: {
        y: curY,
        height: 0,
      },
      exercises: [],
    };
    curY += 1; // Section title

    // For session view alignment, we need to calculate heights based on whether
    // ANY workout has this section expanded. If all workouts have it collapsed
    // OR are read only and have no exercises,
    // we can skip exercises. Otherwise, we include exercises for alignment.
    // The actual rendering will handle per-workout collapse state.
    let allSectionsAtIndexShouldBeSkipped = true;
    for (const workout of workouts) {
      const section = workout.sections[sectionIndex];

      // if section shouldn't be skipped, flag it
      if (
        section &&
        !collapsedSections.has(getKeyForSectionCollapse(section, profileId)) &&
        !(workout.isReadOnly && section.entries.length === 0)
      ) {
        allSectionsAtIndexShouldBeSkipped = false;
        break;
      }
    }

    // Skip exercises only if ALL workouts have this section collapsed
    if (!allSectionsAtIndexShouldBeSkipped) {
      for (const exercise of exerciseHeightsInSection) {
        newSectionYs.exercises.push({ y: curY, height: exercise.height });
        curY += exercise.height;
      }
    }

    // Skip the add-exercise row if all sections are collapsed or every workout
    // is read only (no editable workout renders an add-exercise cell).
    if (!allWorkoutsReadOnly && !allSectionsAtIndexShouldBeSkipped) {
      curY += 1; // Add exercise cell
    }
    newSectionYs.sectionData.height = curY - initY;
    sectionAndExerciseYs.push(newSectionYs);
  }
  if (!allWorkoutsReadOnly) {
    curY += 1; // Add section cell unless every workout is read only
  }

  return { maxY: Math.max(curY, maxY), sectionAndExerciseYs };
}

// Utility function to calculate grid styles for a workout template
export const getWorkoutTemplateGridStyle = (
  uniformWorkoutWidth: number,
): {
  style: CSSProperties;
  overrideGridStyles: boolean;
} => ({
  style: {
    gridColumnStart: 1,
    gridColumnEnd: `span ${uniformWorkoutWidth}`,
  },
  overrideGridStyles: true,
});
